import { Fragment, h } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { GlobalState } from '..';
import { Link, Outlet, isRouteErrorResponse, useNavigate, useParams } from 'react-router-dom';
import { RemixIcon } from '../utils/RemixIcon';
import { LoadingSpinner, getAPI, postAPI, useQuery } from '../utils/utils';
import { ChangeEvent } from 'preact/compat';
import { AppDatePicker } from '../utils/datepicker';
import { License, LicenseStats, LicenseStatsNew, StatsItem } from 'src/common/types';

import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';



ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);



export function StatsLicenseGlobal(props: { state: GlobalState }) {
    
    const urlParams = useParams();
    
    return <Fragment>
        
        {urlParams.licenseId}
        
    </Fragment>;
    
}

export function StatsGlobal(props: { state: GlobalState }) {
    
    const urlParams = useParams();
    
    return <Fragment>
        
        <div className='lisu-card m-3'>
            
            
            
        </div>
        
    </Fragment>
    
}

export function LicenseStatsCard(props: { state: GlobalState, orderId: string, isAdmin?: boolean }) {
    
    const { orderId } = props;
    const [timeRange, setTimeRange] = useState<[number, number]>([Date.now() - 1000*60*60*24*28, Date.now()]);
    
    const { error: errorLicense, result: licenseData, isLoading: isLoadingLicense } = useQuery<License>(`/api/getLicense/${encodeURIComponent(orderId)}`);
    const { error: errorStats, result: statsData, isLoading: isLoadingStats } = useQuery<LicenseStatsNew>(`/api/getCombinedLicenseStats/${encodeURIComponent(orderId)}?from=${timeRange[0]}&to=${timeRange[1]}`);
    
    const isLoading = useMemo<boolean>(() => isLoadingLicense || isLoadingStats, [isLoadingLicense, isLoadingStats]);
    const error = useMemo<string | null>(() => errorLicense ?? errorStats ?? null, [errorLicense, errorStats]);
    
    const statsTimeData = useMemo(() => Object.keys(statsData?.data ?? {}).toSorted(), [statsData]);
    const statsUniqueUsers = useMemo(() => [...(new Set(Object.values(statsData?.data ?? {}).map(r => [...r.usersNew, ...r.usersLegacy]).flat()))], [statsData]);
    
    if (error || !licenseData) {
        return <div className='alert alert-danger m-3'>{error}</div>;
    }
    
    return <div className='lisu-card m-3'>
        
        <div className='d-flex justify-content-between'>
            <div>
                <h4>
                    Tilastot / {licenseData.licenseeName ?? licenseData.orderId}
                    {' '}<RemixIcon icon='ri-admin-line'/>
                </h4>
            </div>
            <div>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24, Date.now()]);
                }}
                >24 h</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*7, Date.now()]);
                }}
                >7 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*14, Date.now()]);
                }}
                >14 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*28, Date.now()]);
                }}
                >28 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*365, Date.now()]);
                }}
                >1 vuosi</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([0, Date.now()]);
                }}
                >kaikki</button>
            </div>
        </div>
        
        {isLoading
            ? <LoadingSpinner/>
            : <Fragment>
                <p className=''>
                    <RemixIcon icon='ri-calendar-2-line' className='me-1'/>
                    <strong>{(new Date(timeRange[0])).toLocaleString('fi')}</strong>
                    {' '}–{' '}
                    <strong>{(new Date(timeRange[1])).toLocaleString('fi')}</strong>
                </p>
                
                <p>Tuloksena {`${(Object.keys(statsData?.data ?? {})).length}`} riviä.</p>
                <p>Tarkasteluajalla {statsUniqueUsers.length} uniikkia käyttäjää ({( (statsUniqueUsers.length / (licenseData.userLimit ?? -1))*100 ).toFixed(1)} %).</p>
                
                <div className='row'>
                    
                    {statsData && <Fragment>
                        <Bar
                            options={{
                                animation: false,
                                responsive: true,
                                plugins: {
                                    legend: {
                                        position: 'top' as const,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Käynnistykset',
                                    },
                                },
                                scales: {
                                    x: {
                                        stacked: true
                                    },
                                    y: {
                                        stacked: true
                                    }
                                }
                            }}
                            data={{
                                labels: statsTimeData.map(en => new Date(en).toLocaleDateString('fi')) ?? [],
                                datasets: [
                                    {
                                        xAxisID: 'x',
                                        label: `Käynnistykset (uusi)`,
                                        data: statsTimeData.map(en => ({ x: new Date(en), y: statsData.data[en].bootsNew })) ?? [],
                                        backgroundColor: '#88db2aff'
                                    },
                                    {
                                        xAxisID: 'x',
                                        label: `Käynnistykset (vanha)`,
                                        data: statsTimeData.map(en => ({ x: new Date(en), y: statsData.data[en].bootsLegacy })) ?? [],
                                        backgroundColor: '#69836eff'
                                    }
                                ]
                            }}
                            />
                        <Bar
                            options={{
                                animation: false,
                                responsive: true,
                                plugins: {
                                    legend: {
                                        position: 'top' as const,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Käyttäjät',
                                    },
                                },
                                scales: {
                                    x: {
                                        stacked: true
                                    },
                                    y: {
                                        stacked: true
                                    }
                                }
                            }}
                            data={{
                                labels: statsTimeData.map(en => new Date(en).toLocaleDateString('fi')) ?? [],
                                datasets: [
                                    {
                                        xAxisID: 'x',
                                        label: `Käyttäjät (uusi)`,
                                        data: statsTimeData.map(en => ({ x: new Date(en), y: statsData.data[en].usersNew.length })) ?? [],
                                        backgroundColor: '#f03bbaff'
                                    },
                                    {
                                        xAxisID: 'x',
                                        label: `Käyttäjät (vanha)`,
                                        data: statsTimeData.map(en => ({ x: new Date(en), y: statsData.data[en].usersLegacy.length })) ?? [],
                                        backgroundColor: '#7f6872ff'
                                    }
                                ]
                            }}
                            />
                    </Fragment>}
                    
                    <div className='col-12 my-2'>
                    </div>
                    
                </div>
                
            </Fragment>}
        
    </div>
    
}

export function LicenseStatsCardOld(props: { state: GlobalState, orderId: string, isAdmin?: boolean }) {
    
    const { orderId } = props;
    const [timeRange, setTimeRange] = useState<[number, number]>([Date.now() - 1000*60*60*24*28, Date.now()]);
    
    const { error: errorLicense, result: licenseData, isLoading: isLoadingLicense } = useQuery<License>(`/api/getLicense/${encodeURIComponent(orderId)}`);
    const { error: errorStats, result: statsData, isLoading: isLoadingStats } = useQuery<LicenseStats>(`/api/getLicenseStats/${encodeURIComponent(orderId)}?from=${timeRange[0]}&to=${timeRange[1]}`);
    
    const isLoading = useMemo<boolean>(() => isLoadingLicense || isLoadingStats, [isLoadingLicense, isLoadingStats]);
    const error = useMemo<string | null>(() => errorLicense ?? errorStats ?? null, [errorLicense, errorStats]);
    
    const statsTimeData = useMemo(() => fillDateGaps(statsData?.bootsPerDay ?? []), [statsData]);
    
    if (error || !licenseData) {
        return <div className='alert alert-danger m-3'>{error}</div>;
    }
    
    return <div className='lisu-card m-3'>
        
        <div className='d-flex justify-content-between'>
            <div>
                <h4>
                    Tilastot / {orderId}
                    {' '}<RemixIcon icon='ri-admin-line'/>
                </h4>
            </div>
            <div>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24, Date.now()]);
                }}
                >24 h</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*7, Date.now()]);
                }}
                >7 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*14, Date.now()]);
                }}
                >14 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*28, Date.now()]);
                }}
                >28 vrk</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([Date.now() - 1000*60*60*24*365, Date.now()]);
                }}
                >1 vuosi</button>
            <button className='btn btn-xsm btn-outline-secondary me-1'
                onClick={e => {
                    e.preventDefault();
                    setTimeRange([0, Date.now()]);
                }}
                >kaikki</button>
            </div>
        </div>
        
        {isLoading
            ? <LoadingSpinner/>
            : <Fragment>
                <p className=''>
                    <RemixIcon icon='ri-calendar-2-line' className='me-1'/>
                    <strong>{(new Date(timeRange[0])).toLocaleString('fi')}</strong>
                    {' '}–{' '}
                    <strong>{(new Date(timeRange[1])).toLocaleString('fi')}</strong>
                </p>
                
                <div className='row'>
                    
                    <div className='col-12 my-2'>
                        <h5>Käynnistykset</h5>
                        <Bar
                            options={{
                                animation: false,
                                responsive: true,
                                plugins: {
                                    legend: {
                                        position: 'top' as const,
                                    },
                                    title: {
                                        display: true,
                                        text: 'Käynnistykset',
                                    },
                                },
                            }}
                            data={{
                                labels: statsTimeData.map(en => new Date(en.statKey).toLocaleDateString('fi')) ?? [],
                                datasets: [{
                                    xAxisID: 'x',
                                    label: `Käynnistykset`,
                                    data: statsTimeData.map(en => ({ x: new Date(en.statKey), y: en.statCount })) ?? [],
                                    backgroundColor: '#32a8a8'
                                }]
                            }}
                            />
                    </div>
                    
                    <div className='col-12 col-lg-6 my-2'>
                        <h5>Päätelaitteet</h5>
                        <div className='lisu-card p-0 overflow-scroll' style={{ maxHeight: '400px' }}>
                            <StatsTable
                                data={statsData?.hostnames ?? []}
                                keyLabel='Hostname'
                                valueLabel='Käynnistykset'
                                linkPattern={key => props.isAdmin
                                        ? `/app/admin/stats/license/${encodeURIComponent(orderId)}/hostname/${encodeURIComponent(key)}`
                                        : `/app/stats/license/${encodeURIComponent(orderId)}/hostname/${encodeURIComponent(key)}`}
                                />
                        </div>
                    </div>
                    
                    <div className='col-12 col-lg-6 my-2'>
                        <h5>Käyttäjät</h5>
                        <div className='lisu-card p-0 overflow-scroll' style={{ maxHeight: '400px' }}>
                            <StatsTable
                                data={statsData?.users ?? []}
                                keyLabel='Käyttäjä'
                                valueLabel='Käynnistykset'
                                isMonoKey={true}
                                linkPattern={key => props.isAdmin
                                    ? `/app/admin/stats/license/${encodeURIComponent(orderId)}/user/${encodeURIComponent(key)}`
                                    : `/app/stats/license/${encodeURIComponent(orderId)}/user/${encodeURIComponent(key)}`}
                                />
                        </div>
                    </div>
                    
                    <div className='col-12 col-lg-6 my-2'>
                        <h5>IP-osoitteet</h5>
                        <div className='lisu-card p-0 overflow-scroll' style={{ maxHeight: '400px' }}>
                            <StatsTable
                                data={statsData?.ips ?? []}
                                keyLabel='IP-osoite'
                                valueLabel='Käynnistykset'
                                isMonoKey={true}
                                linkPattern={key => props.isAdmin
                                    ? `/app/admin/stats/license/${encodeURIComponent(orderId)}/ip/${encodeURIComponent(key)}`
                                    : `/app/stats/license/${encodeURIComponent(orderId)}/ip/${encodeURIComponent(key)}`}
                                />
                        </div>
                    </div>
                    
                    <div className='col-12 col-lg-6 my-2'>
                        <h5>Ohjelmistoversiot</h5>
                        <div className='lisu-card p-0 overflow-scroll' style={{ maxHeight: '400px' }}>
                            <StatsTable
                                data={statsData?.versions ?? []}
                                keyLabel='Versio'
                                valueLabel='Käynnistykset'
                                isMonoKey={true}
                                linkPattern={key => props.isAdmin
                                    ? `/app/admin/stats/license/${encodeURIComponent(orderId)}/version/${encodeURIComponent(key)}`
                                    : `/app/stats/license/${encodeURIComponent(orderId)}/version/${encodeURIComponent(key)}`}
                                />
                        </div>
                    </div>
                    
                </div>
                
            </Fragment>}
        
    </div>
    
}



export function fillDateGaps(data: StatsItem[], interval: number = 1000*60*60*24) {
    
    if (data.length == 0 || data.length == 1) {
        return data;
    }
    
    const startDate = new Date(data[0].statKey);
    const endDate = new Date(data[data.length-1].statKey);
    const datesByKey = Object.fromEntries(data.map(en => [en.statKey, en.statCount]));
    
    const out: StatsItem[] = [];
    for (let dt = startDate.getTime(); dt <= endDate.getTime(); dt += interval) {
        const dateKey = (new Date(dt)).toISOString().substring(0, 10);
        if (datesByKey[dateKey]) {
            out.push({ statKey: dateKey, statCount: datesByKey[dateKey] });
        } else {
            out.push({ statKey: dateKey, statCount: 0 });
        }
    }
    
    return out;
    
}

export function StatsTable(props: {data: StatsItem[], keyLabel?: any, valueLabel?: any, isMonoKey?: boolean, linkPattern?: (s: string) => string }) {
    
    return <table className='table table-sm mb-0'>
        <thead className='sticky-top'>
            <tr className='table-secondary'>
                <th>{props.keyLabel ?? <RemixIcon icon='ri-key-line'/>}</th>
                <th className='text-end'>{props.valueLabel ?? <RemixIcon icon='ri-numbers-line'/>}</th>
            </tr>
        </thead>
        <tbody>
            {props.data.length == 0
                ? <tr><td colSpan={2} className='text-center'><i>(ei dataa)</i></td></tr>
                : ''}
            {props.data.map(row =>
                <tr key={row.statKey}>
                    <td className={props.isMonoKey ? 'mono' : ''}>
                        {props.linkPattern
                            ? <Link to={props.linkPattern(row.statKey)}>{row.statKey}</Link>
                            : <span>{row.statKey}</span>}
                    </td>
                    <td className='text-end'>{row.statCount}</td>
                </tr>
            )}
        </tbody>
        <tfoot className='sticky-bottom'>
            <tr className='table-secondary'>
                <td><b>Yhteensä</b></td>
                <td className='text-end'>{props.data.reduce((prev, curr) => prev+curr.statCount, 0)}</td>
            </tr>
            <tr className='table-secondary'>
                <td><b>Avaimien lkm</b></td>
                <td className='text-end'>{props.data.length} kpl</td>
            </tr>
        </tfoot>
    </table>;
    
}